import './navbar.css';
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const edit_api = 'https://liedersammlung.com/edit_add_api.php';
const categories = [
  'auferstehung',
  'evangelisation',
  'gebet',
  'gnade',
  'golgatha',
  'himmlische_heimat',
  'hingabe_und_dienst',
  'kampf_und_sieg',
  'kinderlieder',
  'lob_und_anbetung',
  'trost_und_vertrauen',
  'wiederkunft_christi',
  'wort_gottes'
];

function formatCategoryLabel(value) {
  if (!value) return '';
  const words = value.replace(/_/g, ' ').split(' ').filter(Boolean);
  const lowerWords = new Set(['und', 'oder', 'von', 'vom', 'im', 'am', 'zu', 'zur', 'der', 'die', 'das']);
  return words
    .map((word, index) => {
      const lower = word.toLowerCase();
      if (index > 0 && lowerWords.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

function toAudioSlug(value) {
  if (!value) return '';
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\u00DF/g, 'ss')
    .replace(/[^a-z0-9]+/g, '');
}

const audioContext = require.context('../audio', false, /\.mp3$/);
const audioFilesBySlug = audioContext.keys().reduce((result, filePath) => {
  const rawName = filePath.replace('./', '').replace(/\.mp3$/i, '');
  result[toAudioSlug(rawName)] = audioContext(filePath);
  return result;
}, {});

function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const isSongPage = location.pathname.startsWith('/lied/');
  const isIndexPage = location.pathname === '/';
  const isEditPage = location.pathname.startsWith('/bearbeiten');
  const isAddPage = location.pathname.startsWith('/hinzufuegen');
  const [showCategoryMenu, setShowCategoryMenu] = useState(false);
  const [showHamburgerMenu, setShowHamburgerMenu] = useState(false);
  const [audioSrc, setAudioSrc] = useState('');
  const [showAudioBar, setShowAudioBar] = useState(false);
  const [showNoMusicModal, setShowNoMusicModal] = useState(false);
  const audioRef = useRef(null);
  const categoryMenuRef = useRef(null);
  const hamburgerMenuRef = useRef(null);
  const selectedCategory = new URLSearchParams(location.search).get('kategorie') || '';

  useEffect(() => {
    if (!isSongPage) {
      setShowAudioBar(false);
      setAudioSrc('');
      if (audioRef.current) {
        audioRef.current.pause();
      }
    }
  }, [isSongPage]);

  useEffect(() => {
    setShowCategoryMenu(false);
    setShowHamburgerMenu(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!showCategoryMenu && !showHamburgerMenu) return;
      if (event.target.closest('.nav_category') || event.target.closest('.nav_hamburger')) return;
      if (categoryMenuRef.current && !categoryMenuRef.current.contains(event.target)) {
        setShowCategoryMenu(false);
      }
      if (hamburgerMenuRef.current && !hamburgerMenuRef.current.contains(event.target)) {
        setShowHamburgerMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCategoryMenu, showHamburgerMenu]);

  function handleCategorySelect(category) {
    const params = new URLSearchParams();
    if (category) {
      params.set('kategorie', category);
    }

    const search = params.toString();
    navigate({
      pathname: '/kategorie',
      search: search ? `?${search}` : ''
    });
    setShowCategoryMenu(false);
  }

  function handleHamburgerToggle() {
    setShowHamburgerMenu((prev) => {
      if (!prev) setShowCategoryMenu(false);
      return !prev;
    });
  }

  function handleAddSong() {
    setShowHamburgerMenu(false);
    navigate('/hinzufuegen');
  }

  function handleEditSong() {
    setShowHamburgerMenu(false);
    navigate('/bearbeiten');
  }

  async function handleMusicClick() {
    const pathParts = location.pathname.split('/');
    const songId = pathParts[2];

    if (!songId) {
      setShowNoMusicModal(true);
      return;
    }

    try {
      const response = await fetch(`${edit_api}?action=get&id=${encodeURIComponent(songId)}`);
      const data = await response.json();
      const record = Array.isArray(data) ? data[0] : data;

      if (record?.audio_url) {
        setAudioSrc(record.audio_url);
        setShowAudioBar(true);
        return;
      }

      const songSlug = toAudioSlug(record?.name);

      if (audioFilesBySlug[songSlug]) {
        setAudioSrc(audioFilesBySlug[songSlug]);
        setShowAudioBar(true);
        return;
      }

      setShowNoMusicModal(true);
    } catch (err) {
      setShowNoMusicModal(true);
    }
  }

  return (
    <>
      {showNoMusicModal && (
        <div className='modal_overlay' onClick={() => setShowNoMusicModal(false)}>
          <div className='modal_dialog' onClick={(event) => event.stopPropagation()}>
            <p className='modal_text'>
              Für dieses Lied ist derzeit keine Musik verfügbar. Wenn du eine passende Aufnahme hast, kannst du sie unter "Lied bearbeiten" hinzufügen.
            </p>
            <button type='button' className='modal_button' onClick={() => setShowNoMusicModal(false)}>
              Schließen
            </button>
          </div>
        </div>
      )}
      <div className={`audio_bar${showAudioBar ? ' is_visible' : ''}`}>
        {audioSrc && (
          <>
          <audio ref={audioRef} className='audio_player' src={audioSrc} controls autoPlay />
          <button type='button' className='audio_close' onClick={() => setShowAudioBar(false)}>
            close
          </button>
          </>
        )}
      </div>
      {!isSongPage && (
        <div className={`category_panel${showCategoryMenu ? ' is_open' : ''}`} ref={categoryMenuRef}>
          {categories.map((category) => (
            <button
              key={category}
              type='button'
              className={`category_item${selectedCategory === category ? ' is_active' : ''}`}
              onClick={() => handleCategorySelect(category)}
            >
              {formatCategoryLabel(category)}
            </button>
          ))}
        </div>
      )}
      <div ref={hamburgerMenuRef} className={`hamburger_menu${showHamburgerMenu ? ' is_open' : ''}`}>
        <button type='button' className='hamburger_menu_item' onClick={handleEditSong}>
          Lied bearbeiten
        </button>
        <button type='button' className='hamburger_menu_item' onClick={handleAddSong}>
          Lied hinzufügen
        </button>
      </div>
      <div id='navbar_body'>
        <Link className='nav_item' to='/' aria-label='Startseite'>
          home
        </Link>
        {!(isEditPage || isAddPage) && (
          <Link className='nav_item' to='/favoriten' aria-label='Favoriten'>
            favorite
          </Link>
        )}
        {!isSongPage && !(isEditPage || isAddPage) && (
          <button
            type='button'
            className={`nav_item nav_category${showCategoryMenu ? ' is_open' : ''}`}
            aria-label='Kategorien'
            onClick={() => {
              setShowCategoryMenu((prev) => {
                if (!prev) setShowHamburgerMenu(false);
                return !prev;
              });
            }}
          >
            category
          </button>
        )}
        {(isIndexPage || isEditPage || isAddPage) && (
          <button
            type='button'
            className={`nav_item nav_hamburger${showHamburgerMenu ? ' is_open' : ''}`}
            aria-label='Menü öffnen'
            onClick={handleHamburgerToggle}
          >
            <span className='hamburger_line line1' />
            <span className='hamburger_line line2' />
            <span className='hamburger_line line3' />
          </button>
        )}
        {isSongPage && (
          <button type='button' className='nav_item nav_music' aria-label='Musik abspielen' onClick={handleMusicClick}>
            music_note
          </button>
        )}
      </div>
    </>
  );
}

export default Navbar;
